import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import soundtrack from "./assets/soundtrack.mp3";

// Renders the Bouncing Ball arcade game using Three.js: paddle, ball, bricks, particle effects, and looping soundtrack.
// Supports infinite level progression: when all bricks are destroyed the next level begins with a fresh brick layout
// and a slightly faster ball.
// Visual upgrades: synthwave glow via UnrealBloomPass, perimeter wall borders, 3D layered paddle and bricks.
export default function BouncingBalls() {
  const mountRef = useRef(null);
  const moveDir = useRef(0);
  // Tracks whether the paddle is currently controlled by an active touch gesture.
  const touchControlActiveRef = useRef(false);
  // Stores the latest touch X coordinate in viewport space for direct paddle mapping.
  const touchTargetClientXRef = useRef(0);
  const scoreRef = useRef(0);
  const livesRef = useRef(3);
  const restartGameRef = useRef(() => {});
  const levelTransitionTimeoutRef = useRef(null);
  const launchTimeoutRef = useRef(null);
  const gamePhaseRef = useRef("waiting-to-launch");
  // Holds the looping soundtrack Audio element for continuous playback.
  const soundtrackRef = useRef(null);

  // levelRef mirrors the React level state so the Three.js animation loop can read the current level
  // without closure staleness.
  const levelRef = useRef(1);

  // baseSpeed stores the ball velocity magnitude for the current level so we can increase it per level.
  const baseSpeedRef = useRef(1);

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isLevelTransitioning, setIsLevelTransitioning] = useState(false);

  useEffect(() => {
    // Reads the current mount container size so Three.js always fits the actual available area.
    function getMountSize() {
      const mount = mountRef.current;
      const width = mount?.clientWidth || window.innerWidth;
      const height = mount?.clientHeight || window.innerHeight;
      return {
        width: Math.max(1, width),
        height: Math.max(1, height)
      };
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050010);
    scene.fog = new THREE.FogExp2(0x050010, 0.04);

    const initialSize = getMountSize();

    const camera = new THREE.PerspectiveCamera(
      70,
      initialSize.width / initialSize.height,
      0.1,
      1000
    );
    camera.position.z = 10;

    // Enable tone mapping so bloom colours look more like real neon glow.
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(initialSize.width, initialSize.height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ReinhardToneMapping;
    renderer.toneMappingExposure = 1.2;
    mountRef.current.appendChild(renderer.domElement);

    /* POST-PROCESSING - EffectComposer with UnrealBloomPass for synthwave neon glow */
    const composer = new EffectComposer(renderer);

    // RenderPass renders the scene normally as the base layer.
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    // UnrealBloomPass adds the glowing halo around bright / emissive objects.
    // Parameters: resolution, strength, radius, threshold.
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(initialSize.width, initialSize.height),
      0.2,   // bloom strength (reduced to soften overall glow)
      0.2,   // bloom radius (tighter bloom spread)
      0.2    // luminance threshold (higher so only brighter surfaces bloom)
    );
    composer.addPass(bloomPass);

    // OutputPass converts the HDR buffer to the correct sRGB output for the display.
    const outputPass = new OutputPass();
    composer.addPass(outputPass);

    /* LIGHTS */
    const ambient = new THREE.AmbientLight(0x220033, 1.0);
    scene.add(ambient);

    // Magenta point light from above to colour neon surfaces.
    const point = new THREE.PointLight(0xff00ff, 3, 40);
    point.position.set(0, 6, 6);
    scene.add(point);

    // Cyan fill light from below to give the arena a dual-tone synthwave feel.
    const fillLight = new THREE.PointLight(0x00ffff, 1.5, 30);
    fillLight.position.set(0, -6, 4);
    scene.add(fillLight);

    /* SYNTHWAVE GRID */
    const grid = new THREE.GridHelper(40, 40, 0xff00ff, 0x440044);
    grid.position.y = -5;
    grid.rotation.x = Math.PI / 2;
    scene.add(grid);

    /* PERIMETER WALLS - visible neon border lines that mark the playable arena bounds.
       The play area spans X: -5 to +5 and Y: -5 to +5.
       We add glowing thin box meshes along each edge so players can clearly see the limits. */

    // Reusable material for all wall segments; strong emissive so bloom picks it up well.
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0xff00ff,
      emissive: 0xff00ff,
      emissiveIntensity: 1.4,
      roughness: 0.3,
      metalness: 0.8
    });

    const wallThickness = 0.12;
    const wallDepth = 0.4;
    const arenaHalfHeight = 5;
    const arenaHalfWidth = 5;
    const arenaH = arenaHalfHeight * 2; // arena height (-5 to +5)
    const arenaW = arenaHalfWidth * 2; // arena width  (-5 to +5)
    const cameraFitMargin = 1.06;
    const requiredViewHalfWidth = arenaHalfWidth + wallThickness;
    const requiredViewHalfHeight = arenaHalfHeight + wallThickness;

    // Calculates the camera distance needed to fit the entire arena extents for the current aspect ratio.
    function computeCameraDistanceToFitArena(aspectRatio) {
      const safeAspectRatio = Math.max(0.0001, aspectRatio);
      const verticalFovRadians = THREE.MathUtils.degToRad(camera.fov);
      const tanHalfVerticalFov = Math.tan(verticalFovRadians / 2);

      const distanceToFitHeight = requiredViewHalfHeight / tanHalfVerticalFov;
      const distanceToFitWidth = requiredViewHalfWidth / (tanHalfVerticalFov * safeAspectRatio);

      return Math.max(distanceToFitHeight, distanceToFitWidth) * cameraFitMargin;
    }

    // Applies responsive camera framing so the full arena stays visible after size/orientation changes.
    function applyResponsiveCameraFraming(width, height) {
      const safeHeight = Math.max(1, height);
      const aspectRatio = width / safeHeight;

      camera.aspect = aspectRatio;
      camera.position.z = computeCameraDistanceToFitArena(aspectRatio);
      camera.updateProjectionMatrix();
    }

    // Inner horizontal walls represent the actual playable X perimeter for the paddle.
    const leftInnerWallX = -arenaHalfWidth + wallThickness / 2;
    const rightInnerWallX = arenaHalfWidth - wallThickness / 2;

    // Left wall segment (vertical bar along x = -5).
    const leftWallGeo = new THREE.BoxGeometry(wallThickness, arenaH, wallDepth);
    const leftWall = new THREE.Mesh(leftWallGeo, wallMat);
    leftWall.position.set(-arenaHalfWidth, 0, 0);
    scene.add(leftWall);

    // Right wall segment (vertical bar along x = +5).
    const rightWallGeo = new THREE.BoxGeometry(wallThickness, arenaH, wallDepth);
    const rightWall = new THREE.Mesh(rightWallGeo, wallMat);
    rightWall.position.set(arenaHalfWidth, 0, 0);
    scene.add(rightWall);

    // Top wall segment (horizontal bar along y = +5).
    const topWallGeo = new THREE.BoxGeometry(arenaW + wallThickness * 2, wallThickness, wallDepth);
    const topWall = new THREE.Mesh(topWallGeo, wallMat);
    topWall.position.set(0, arenaHalfHeight, 0);
    scene.add(topWall);

    // Bottom visual border – purely decorative, does not affect collision (ball still loses life below y=-5).
    // Uses cyan colour to contrast with the magenta top/side walls.
    const bottomWallMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 1.1,
      roughness: 0.3,
      metalness: 0.8
    });
    const bottomWallGeo = new THREE.BoxGeometry(arenaW + wallThickness * 2, wallThickness, wallDepth);
    const bottomWall = new THREE.Mesh(bottomWallGeo, bottomWallMat);
    bottomWall.position.set(0, -arenaHalfHeight, 0);
    scene.add(bottomWall);

    // Corner cap meshes to fill the small gaps at the four corners.
    const cornerGeo = new THREE.BoxGeometry(wallThickness, wallThickness, wallDepth);
    const tlCorner = new THREE.Mesh(cornerGeo, wallMat);
    tlCorner.position.set(-arenaHalfWidth, arenaHalfHeight, 0);
    scene.add(tlCorner);

    const trCorner = new THREE.Mesh(cornerGeo, wallMat);
    trCorner.position.set(arenaHalfWidth, arenaHalfHeight, 0);
    scene.add(trCorner);

    /* EDGE LINE HELPERS along walls – extremely thin EdgesGeometry lines for extra crispness */
    // Adds a glowing white wireframe outline over the top wall for sharp edge definition.
    const topEdges = new THREE.EdgesGeometry(topWallGeo);
    const edgeLineMat = new THREE.LineBasicMaterial({ color: 0xffffff });
    const topWallEdge = new THREE.LineSegments(topEdges, edgeLineMat);
    topWallEdge.position.copy(topWall.position);
    scene.add(topWallEdge);

    const leftEdges = new THREE.EdgesGeometry(leftWallGeo);
    const leftWallEdge = new THREE.LineSegments(leftEdges, edgeLineMat.clone());
    leftWallEdge.position.copy(leftWall.position);
    scene.add(leftWallEdge);

    const rightEdges = new THREE.EdgesGeometry(rightWallGeo);
    const rightWallEdge = new THREE.LineSegments(rightEdges, edgeLineMat.clone());
    rightWallEdge.position.copy(rightWall.position);
    scene.add(rightWallEdge);

    /* PADDLE - layered 3D construction: a main body slab + a thinner highlight strip on top
       to create a bevel/depth effect typical of chunky arcade hardware. */

    // Main body – taller and deeper than before to feel more substantial.
    const paddleBodyGeo = new THREE.BoxGeometry(2.2, 0.38, 0.55);
    const paddleBodyMat = new THREE.MeshStandardMaterial({
      color: 0x007799,
      emissive: 0x00cccc,
      emissiveIntensity: 0.8,
      roughness: 0.25,
      metalness: 0.9
    });
    const paddleBody = new THREE.Mesh(paddleBodyGeo, paddleBodyMat);

    // Top highlight strip – bright cyan, sits just above the body to simulate a glowing bevel edge.
    const paddleHighlightGeo = new THREE.BoxGeometry(2.1, 0.06, 0.55);
    const paddleHighlightMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 3.0,
      roughness: 0.1,
      metalness: 1.0
    });
    const paddleHighlight = new THREE.Mesh(paddleHighlightGeo, paddleHighlightMat);
    paddleHighlight.position.y = 0.22; // sits on top of the body

    // Group the two paddle parts so they move as one unit.
    const paddle = new THREE.Group();
    paddle.add(paddleBody);
    paddle.add(paddleHighlight);
    paddle.position.y = -4;
    scene.add(paddle);

    // Paddle clamp uses actual geometry and inner wall faces so visuals and movement bounds match.
    const paddleHalfWidth = paddleBodyGeo.parameters.width / 2;
    const paddleSafetyMargin = 0.01;
    const paddleMinX = leftInnerWallX + paddleHalfWidth + paddleSafetyMargin;
    const paddleMaxX = rightInnerWallX - paddleHalfWidth - paddleSafetyMargin;

    // Thin edge wireframe overlay on paddle body for crisp 3D silhouette.
    const paddleEdges = new THREE.EdgesGeometry(paddleBodyGeo);
    const paddleEdgeLine = new THREE.LineSegments(
      paddleEdges,
      new THREE.LineBasicMaterial({ color: 0x00ffff })
    );
    paddleBody.add(paddleEdgeLine);

    /* BALL */
    const ballRadius = 0.28;
    const ballGeo = new THREE.SphereGeometry(ballRadius, 32, 32);
    const ballMat = new THREE.MeshStandardMaterial({
      color: 0xff00ff,
      emissive: 0xff00ff,
      emissiveIntensity: 1.5,
      roughness: 0.1,
      metalness: 0.8
    });

    const ball = new THREE.Mesh(ballGeo, ballMat);
    scene.add(ball);

    // Base ball velocity for level 1.
    // Tuned down from the previous value because gameplay became too fast.
    const baseBallVelocity = new THREE.Vector3(0.09, 0.105, 0);

    // velocity is a mutable reference so resetBall and level-up can both update it.
    let velocity = baseBallVelocity.clone();

    /* BRICKS - shared mutable array used by the animation loop and spawnBricks.
       Each brick is a Group containing: a main body mesh, a highlight strip, and edge lines.
       The group's position is used for collision detection. */
    const bricks = [];

    // Shared geometry for the brick body; individual materials are created per brick
    // so each one can have its own colour while sharing geometric data.
    const brickBodyGeo = new THREE.BoxGeometry(1.5, 0.6, 0.55);
    const brickHighlightGeo = new THREE.BoxGeometry(1.4, 0.07, 0.55);
    const brickEdgesTemplate = new THREE.EdgesGeometry(brickBodyGeo);

    const colors = [0xff0080, 0xff00ff, 0x00ffff, 0xffff00];
    // Matching emissive colours (slightly darker) to keep the glow vivid but not blinding.
    const emissiveColors = [0xcc0060, 0xcc00cc, 0x00cccc, 0xcccc00];

    // Removes all existing brick groups from the scene, clears the bricks array, then spawns
    // the same fixed grid layout used by level 1 so every level has identical brick count.
    function spawnBricks(lvl) {
      // Clean up any surviving brick groups from the previous level to avoid memory leaks.
      for (let i = 0; i < bricks.length; i++) {
        if (bricks[i]) {
          scene.remove(bricks[i]);
          // Traverse the group and dispose each child's geometry and material individually.
          bricks[i].traverse((child) => {
            if (child.isMesh) {
              child.geometry.dispose();
              if (Array.isArray(child.material)) {
                child.material.forEach((m) => m.dispose());
              } else {
                child.material.dispose();
              }
            }
            if (child.isLineSegments) {
              child.geometry.dispose();
              child.material.dispose();
            }
          });
          bricks[i] = null;
        }
      }
      // Reset the array length so old nulls are gone.
      bricks.length = 0;

      // Keep the same number of rows as level 1 for every level.
      const maxRow = 5;

      for (let x = -4; x <= 4; x += 2) {
        for (let y = 1; y <= maxRow; y++) {
          const colorIdx = y % colors.length;

          // Main brick body – metallic slab with strong emissive for bloom pickup.
          const bodyMat = new THREE.MeshStandardMaterial({
            color: colors[colorIdx],
            emissive: emissiveColors[colorIdx],
            emissiveIntensity: 1.0,
            roughness: 0.2,
            metalness: 0.85
          });
          const bodyMesh = new THREE.Mesh(brickBodyGeo.clone(), bodyMat);

          // Highlight strip across the top of the brick to simulate a bevel/chamfer edge.
          const hlMat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            emissive: colors[colorIdx],
            emissiveIntensity: 2.5,
            roughness: 0.05,
            metalness: 1.0
          });
          const hlMesh = new THREE.Mesh(brickHighlightGeo.clone(), hlMat);
          hlMesh.position.y = 0.34; // position at the very top of the body

          // Thin wireframe edge overlay for extra 3D crispness.
          const edgeLine = new THREE.LineSegments(
            brickEdgesTemplate.clone(),
            new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 })
          );

          // Group everything under one object and store the colour index for future reference.
          const brickGroup = new THREE.Group();
          brickGroup.add(bodyMesh);
          brickGroup.add(hlMesh);
          brickGroup.add(edgeLine);
          brickGroup.position.set(x, y, 0);
          brickGroup.userData.colorIdx = colorIdx;

          scene.add(brickGroup);
          bricks.push(brickGroup);
        }
      }
    }

    /* PARTICLE EFFECT */
    // Creates a burst of coloured particles at the given world-space position and removes them
    // automatically after 350 ms so they do not accumulate in the scene.
    function spawnParticles(position, color) {
      const geo = new THREE.BufferGeometry();

      const count = 30;
      const positions = new Float32Array(count * 3);

      // Spread particles randomly around the hit point for a more dynamic explosion look.
      for (let i = 0; i < count; i++) {
        positions[i * 3]     = position.x + (Math.random() - 0.5) * 0.8;
        positions[i * 3 + 1] = position.y + (Math.random() - 0.5) * 0.8;
        positions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 0.3;
      }

      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));

      const mat = new THREE.PointsMaterial({
        color: color || 0xffffff,
        size: 0.18,
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true
      });

      const particles = new THREE.Points(geo, mat);
      scene.add(particles);

      // Remove and dispose the particle system after the effect duration.
      setTimeout(() => {
        scene.remove(particles);
        geo.dispose();
        mat.dispose();
      }, 350);
    }

    // Moves the paddle according to the latest input direction and keeps it inside the inner walls.
    function updatePaddlePosition() {
      if (touchControlActiveRef.current) {
        const mount = mountRef.current;

        if (mount) {
          const rect = mount.getBoundingClientRect();
          const safeWidth = Math.max(1, rect.width);
          const localX = THREE.MathUtils.clamp(
            touchTargetClientXRef.current - rect.left,
            0,
            safeWidth
          );
          const normalizedX = localX / safeWidth;

          // Maps the finger horizontal position directly to paddle world-space X for 1:1 touch tracking.
          paddle.position.x = paddleMinX + normalizedX * (paddleMaxX - paddleMinX);
        }
      } else {
        paddle.position.x += moveDir.current * 0.25;
      }

      paddle.position.x = Math.max(paddleMinX, Math.min(paddleMaxX, paddle.position.x));
    }

    // Places the ball just above the paddle so the player can prepare before launch.
    function parkBallOnPaddle() {
      ball.position.set(paddle.position.x, paddle.position.y + 0.55, 0);
      velocity.set(0, 0, 0);
    }

    // Applies the current level speed multiplier to the base launch velocity.
    function setLaunchVelocityForCurrentLevel() {
      const s = baseSpeedRef.current;
      velocity = new THREE.Vector3(baseBallVelocity.x * s, baseBallVelocity.y * s, 0);
    }

    // Determines whether the ball touches the lower game border.
    // A life is lost only when the sphere reaches the bottom edge.
    function hasBallTouchedFloor(positionY, ballRadius, floorY) {
      return positionY - ballRadius <= floorY;
    }

    // Clears the pending launch countdown timeout so stale launch callbacks cannot fire.
    function clearLaunchTimeout() {
      if (launchTimeoutRef.current) {
        clearTimeout(launchTimeoutRef.current);
        launchTimeoutRef.current = null;
      }
    }

    // Starts a deterministic 2-second launch delay and then switches gameplay to active ball motion.
    function beginLaunchCountdown() {
      clearLaunchTimeout();
      gamePhaseRef.current = "waiting-to-launch";
      parkBallOnPaddle();

      launchTimeoutRef.current = setTimeout(() => {
        if (gamePhaseRef.current !== "waiting-to-launch") return;
        setLaunchVelocityForCurrentLevel();
        gamePhaseRef.current = "active";
        launchTimeoutRef.current = null;
      }, 2000);
    }

    // Advances the game to the next level: increments the level counter, increases
    // ball speed by 10%, and spawns a fresh brick grid.
    function advanceLevel() {
      const nextLevel = levelRef.current + 1;
      levelRef.current = nextLevel;

      // Increase speed by 10% per level.
      baseSpeedRef.current = baseSpeedRef.current * 1.1;

      setLevel(nextLevel);
      spawnBricks(nextLevel);
    }

    // Clears the pending level transition timeout so no stale delayed callbacks run.
    function clearLevelTransitionTimeout() {
      if (levelTransitionTimeoutRef.current) {
        clearTimeout(levelTransitionTimeoutRef.current);
        levelTransitionTimeoutRef.current = null;
      }
    }

    // Handles a bottom-border ball loss exactly once per event and starts either game-over or relaunch flow.
    function handleBallLoss() {
      if (gamePhaseRef.current !== "active") return;

      const nextLives = Math.max(livesRef.current - 1, 0);
      livesRef.current = nextLives;
      setLives(nextLives);

      if (nextLives === 0) {
        triggerGameOver();
        return;
      }

      beginLaunchCountdown();
    }

    // Starts the level-complete transition overlay and advances to the next level after 3 seconds.
    function startLevelTransition() {
      if (gamePhaseRef.current !== "active") return;

      gamePhaseRef.current = "level-transition";
      setIsLevelTransitioning(true);
      clearLaunchTimeout();
      clearLevelTransitionTimeout();

      levelTransitionTimeoutRef.current = setTimeout(() => {
        advanceLevel();
        setIsLevelTransitioning(false);
        beginLaunchCountdown();
        levelTransitionTimeoutRef.current = null;
      }, 3000);
    }

    // Marks the match as finished when no lives remain so the render loop can freeze gameplay.
    function triggerGameOver() {
      gamePhaseRef.current = "game-over";
      moveDir.current = 0;
      clearLaunchTimeout();
      clearLevelTransitionTimeout();
      setIsLevelTransitioning(false);
      setIsGameOver(true);
    }

    // Restarts the match from a clean level-1 setup and synchronizes all refs/state values
    // so the Three.js loop and React HUD read the same fresh gameplay snapshot.
    function restartGame() {
      clearLaunchTimeout();
      clearLevelTransitionTimeout();
      moveDir.current = 0;
      gamePhaseRef.current = "waiting-to-launch";

      scoreRef.current = 0;
      livesRef.current = 3;
      levelRef.current = 1;
      baseSpeedRef.current = 1;

      setScore(0);
      setLives(3);
      setLevel(1);
      setIsGameOver(false);
      setIsLevelTransitioning(false);

      paddle.position.x = 0;
      paddle.position.y = -4;
      spawnBricks(1);
      beginLaunchCountdown();
    }

    // Exposes the in-scope restart logic to the React button without stale closure issues.
    restartGameRef.current = restartGame;

    // Advances active gameplay: ball movement, collisions, scoring, life loss, and level clear checks.
    function updateActiveGameplay() {
      ball.position.add(velocity);

      /* WALL COLLISION */
      const leftBound = -5 + ballRadius;
      const rightBound = 5 - ballRadius;
      const topBound = 5 - ballRadius;
      const floorY = -5;

      if (ball.position.x > rightBound) {
        ball.position.x = rightBound;
        velocity.x *= -1;
      } else if (ball.position.x < leftBound) {
        ball.position.x = leftBound;
        velocity.x *= -1;
      }

      if (ball.position.y > topBound) {
        ball.position.y = topBound;
        velocity.y = -Math.abs(velocity.y);
      }

      // The only valid life-loss condition is crossing/touching the bottom border.
      if (hasBallTouchedFloor(ball.position.y, ballRadius, floorY)) {
        handleBallLoss();
        return;
      }

      /* PADDLE COLLISION - use the bounding sphere of the paddle body for the check */
      if (
        ball.position.distanceTo(paddle.position) < 1.2 &&
        ball.position.y < -3.5 &&
        velocity.y < 0
      ) {
        velocity.y = Math.abs(velocity.y);

        const diff = ball.position.x - paddle.position.x;
        velocity.x += diff * 0.02;
      }

      /* BRICK COLLISION */
      bricks.forEach((brick, i) => {
        if (!brick) return;

        if (ball.position.distanceTo(brick.position) < 1) {
          // Use the colour from userData to tint the particle burst for visual variety.
          const hitColor = colors[brick.userData.colorIdx] || 0xffffff;
          spawnParticles(brick.position, hitColor);

          scene.remove(brick);
          // Traverse and dispose all GPU resources held by this brick group.
          brick.traverse((child) => {
            if (child.isMesh) {
              child.geometry.dispose();
              if (Array.isArray(child.material)) {
                child.material.forEach((m) => m.dispose());
              } else {
                child.material.dispose();
              }
            }
            if (child.isLineSegments) {
              child.geometry.dispose();
              child.material.dispose();
            }
          });
          bricks[i] = null;

          velocity.y *= -1;

          scoreRef.current += 10;
          setScore(scoreRef.current);
        }
      });

      /* LEVEL CLEAR DETECTION - check after all brick collision processing */
      // If every entry in the bricks array is null the level is complete.
      if (bricks.length > 0 && bricks.every((b) => b === null)) {
        startLevelTransition();
      }
    }

    /* ANIMATION LOOP */
    // Runs every frame, updates the currently active game phase deterministically, and renders bloom output.
    function animate() {
      requestAnimationFrame(animate);

      updatePaddlePosition();

      if (gamePhaseRef.current === "waiting-to-launch") {
        parkBallOnPaddle();
      } else if (gamePhaseRef.current === "active") {
        updateActiveGameplay();
      }

      // Use the composer instead of renderer.render so bloom is applied every frame.
      composer.render();
    }

    // Spawns level-1 bricks and starts the first deterministic launch countdown.
    spawnBricks(1);
    beginLaunchCountdown();

    animate();

    // Applies camera framing plus renderer/composer sizing from the mount container for consistent visibility.
    function resizeFromMount() {
      const { width, height } = getMountSize();

      applyResponsiveCameraFraming(width, height);

      renderer.setSize(width, height);
      composer.setSize(width, height);
      bloomPass.resolution.set(width, height);
    }

    resizeFromMount();

    window.addEventListener("resize", resizeFromMount);
    window.addEventListener("orientationchange", resizeFromMount);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && mountRef.current) {
      resizeObserver = new ResizeObserver(() => {
        resizeFromMount();
      });
      resizeObserver.observe(mountRef.current);
    }

    return () => {
      window.removeEventListener("resize", resizeFromMount);
      window.removeEventListener("orientationchange", resizeFromMount);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      restartGameRef.current = () => {};
      clearLaunchTimeout();
      clearLevelTransitionTimeout();

      // Dispose shared geometries and the edge template.
      brickBodyGeo.dispose();
      brickHighlightGeo.dispose();
      brickEdgesTemplate.dispose();

      // Dispose wall geometries and materials.
      leftWallGeo.dispose();
      rightWallGeo.dispose();
      topWallGeo.dispose();
      bottomWallGeo.dispose();
      cornerGeo.dispose();
      wallMat.dispose();
      bottomWallMat.dispose();

      // Dispose edge line resources for walls.
      topEdges.dispose();
      edgeLineMat.dispose();
      leftEdges.dispose();
      rightEdges.dispose();

      // Dispose paddle geometries and materials.
      paddleBodyGeo.dispose();
      paddleHighlightGeo.dispose();
      paddleBodyMat.dispose();
      paddleHighlightMat.dispose();
      paddleEdges.dispose();

      // Dispose ball resources.
      ballGeo.dispose();
      ballMat.dispose();

      // Dispose any remaining brick groups that were not destroyed during gameplay.
      for (let i = 0; i < bricks.length; i++) {
        if (bricks[i]) {
          bricks[i].traverse((child) => {
            if (child.isMesh) {
              child.geometry.dispose();
              if (Array.isArray(child.material)) {
                child.material.forEach((m) => m.dispose());
              } else {
                child.material.dispose();
              }
            }
            if (child.isLineSegments) {
              child.geometry.dispose();
              child.material.dispose();
            }
          });
        }
      }

      // Dispose the post-processing composer.
      composer.dispose();

      if (renderer.domElement && mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }

      renderer.dispose();
    };
  }, []);

  /* SOUNDTRACK - Creates the looping background music once and plays it without resetting between levels. */
  useEffect(() => {
    if (soundtrackRef.current) return;
    const audio = new Audio(soundtrack);
    audio.loop = true;
    audio.volume = 0.6;
    soundtrackRef.current = audio;

    // Starts playback on first user interaction to satisfy browser autoplay policies.
    const handleFirstInteraction = () => {
      audio.play().catch(() => {});
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
    };

    window.addEventListener("click", handleFirstInteraction);
    window.addEventListener("keydown", handleFirstInteraction);
    window.addEventListener("touchstart", handleFirstInteraction);

    return () => {
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
      if (soundtrackRef.current) {
        soundtrackRef.current.pause();
      }
      soundtrackRef.current = null;
    };
  }, []);

  /* INPUTS */
  useEffect(() => {
    const down = (e) => {
      if (e.key === "ArrowLeft") moveDir.current = -1;
      if (e.key === "ArrowRight") moveDir.current = 1;
    };

    const up = () => (moveDir.current = 0);

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);

    // Saves the latest finger position so the render loop can map it directly to paddle X.
    const updateTouchTarget = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      touchControlActiveRef.current = true;
      touchTargetClientXRef.current = e.touches[0].clientX;
      moveDir.current = 0;
    };

    const touchStart = (e) => updateTouchTarget(e);

    const touchMove = (e) => {
      updateTouchTarget(e);
    };

    const touchEnd = () => {
      touchControlActiveRef.current = false;
      moveDir.current = 0;
    };

    const touchCancel = () => {
      touchControlActiveRef.current = false;
      moveDir.current = 0;
    };

    window.addEventListener("touchstart", touchStart);
    window.addEventListener("touchmove", touchMove);
    window.addEventListener("touchend", touchEnd);
    window.addEventListener("touchcancel", touchCancel);

    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);

      window.removeEventListener("touchstart", touchStart);
      window.removeEventListener("touchmove", touchMove);
      window.removeEventListener("touchend", touchEnd);
      window.removeEventListener("touchcancel", touchCancel);
    };
  }, []);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        minHeight: "100vh",
        overflow: "hidden",
        touchAction: "none"
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "calc(12px + env(safe-area-inset-top, 0px))",
          left: "calc(12px + env(safe-area-inset-left, 0px))",
          color: "#00ffff",
          fontFamily: "monospace",
          fontSize: "clamp(14px, 3.5vw, 20px)",
          textShadow: "0 0 10px #00ffff, 0 0 20px #00ffff"
        }}
      >
        SCORE: {score}
      </div>

      <div
        style={{
          position: "absolute",
          top: "calc(12px + env(safe-area-inset-top, 0px))",
          right: "calc(12px + env(safe-area-inset-right, 0px))",
          color: "#ff00ff",
          fontFamily: "monospace",
          fontSize: "clamp(14px, 3.5vw, 20px)",
          textShadow: "0 0 10px #ff00ff, 0 0 20px #ff00ff"
        }}
      >
        LIVES: {lives}
      </div>

      {/* LEVEL HUD - displayed in the centre-top of the screen so it is immediately visible */}
      <div
        style={{
          position: "absolute",
          top: "calc(12px + env(safe-area-inset-top, 0px))",
          left: "50%",
          transform: "translateX(-50%)",
          color: "#ffff00",
          fontFamily: "monospace",
          fontSize: "clamp(14px, 3.5vw, 20px)",
          textShadow: "0 0 10px #ffff00, 0 0 20px #ffff00"
        }}
      >
        LEVEL: {level}
      </div>

      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />

      {isGameOver && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(5, 0, 16, 0.75)",
            zIndex: 10
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 20,
              color: "#ff00ff",
              fontFamily: "monospace",
              fontSize: "clamp(28px, 9vw, 54px)",
              fontWeight: "bold",
              letterSpacing: 4,
              textShadow: "0 0 12px #ff00ff, 0 0 24px #ff00ff, 0 0 40px #00ffff",
              border: "2px solid #00ffff",
              padding: "clamp(12px, 3vw, 20px) clamp(16px, 4vw, 30px)",
              background: "rgba(0, 0, 0, 0.45)",
              boxShadow: "0 0 18px #00ffff, inset 0 0 12px #ff00ff",
              margin: "max(env(safe-area-inset-top, 0px), 8px) max(env(safe-area-inset-right, 0px), 8px) max(env(safe-area-inset-bottom, 0px), 8px) max(env(safe-area-inset-left, 0px), 8px)"
            }}
          >
            <div>GAME OVER</div>
            <button
              type="button"
              onClick={() => restartGameRef.current()}
              style={{
                cursor: "pointer",
                color: "#00ffff",
                background: "rgba(0, 0, 0, 0.55)",
                border: "2px solid #00ffff",
                borderRadius: 6,
                padding: "10px 18px",
                fontFamily: "monospace",
                fontSize: "clamp(16px, 4.2vw, 20px)",
                fontWeight: "bold",
                letterSpacing: 2,
                textShadow: "0 0 8px #00ffff, 0 0 16px #00ffff",
                boxShadow: "0 0 12px #00ffff, inset 0 0 8px #ff00ff"
              }}
            >
              PLAY AGAIN
            </button>
          </div>
        </div>
      )}

      {isLevelTransitioning && !isGameOver && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(5, 0, 16, 0.75)",
            zIndex: 9
          }}
        >
          <div
            style={{
              color: "#00ffff",
              fontFamily: "monospace",
              fontSize: "clamp(28px, 9vw, 54px)",
              fontWeight: "bold",
              letterSpacing: 2,
              textShadow: "0 0 12px #00ffff, 0 0 24px #00ffff, 0 0 40px #ff00ff",
              border: "2px solid #ff00ff",
              padding: "clamp(12px, 3vw, 20px) clamp(16px, 4vw, 30px)",
              background: "rgba(0, 0, 0, 0.45)",
              boxShadow: "0 0 18px #ff00ff, inset 0 0 12px #00ffff",
              margin: "max(env(safe-area-inset-top, 0px), 8px) max(env(safe-area-inset-right, 0px), 8px) max(env(safe-area-inset-bottom, 0px), 8px) max(env(safe-area-inset-left, 0px), 8px)"
            }}
          >
            Level Complete! Ready?
          </div>
        </div>
      )}
    </div>
  );
}
